import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { T } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { Button } from './poyais-ui/primitives';
import { useLang, addTranslations } from './i18n';

addTranslations({
  'Trello bağlantısı başlatılamadı. TRELLO_API_KEY/SECRET tanımlı mı kontrol edin.': 'Could not start the Trello connection. Check that TRELLO_API_KEY/SECRET are configured.',
  'Trello bağlantısı başarısız oldu: ': 'Trello connection failed: ',
  "Trello hesabını bağla — board'larını Panolar'a aktar veya kartlarını Görevler ile eşleştir.": "Connect your Trello account — import your boards into Boards or match your cards with Tasks.",
  'Trello Hesabını Bağla': 'Connect Trello Account',
  'Kartlar çekilemedi.': 'Could not pull cards.',
  "Görev Trello'ya gönderilemedi.": 'Could not push task to Trello.',
  'Bağlı hesap': 'Connected account',
  'Kontrol ediliyor…': 'Checking…',
  'Trello Board': 'Trello Board',
  'Board seç…': 'Select board…',
  "Trello'dan Çek": 'Pull from Trello',
  "Trello'ya Gönder": 'Push to Trello',
  'Kart bulunamadı.': 'No cards found.',
  'zaten aktarıldı': 'already imported',
  'Aktarılıyor…': 'Importing…',
  'Seçilenleri Çek': 'Pull Selected',
  'Görev': 'Task',
  'Görev seç…': 'Select task…',
  'Trello Listesi': 'Trello List',
  'Liste seç…': 'Select list…',
  'Gönderiliyor…': 'Sending…',
  'Trello Board Bağla': 'Connect Trello Board',
  'Board içe aktarılamadı.': 'Could not import board.',
  "Trello'da board oluşturulamadı.": 'Could not create board on Trello.',
  'Trello hesabında board bulunamadı.': 'No boards found in this Trello account.',
  'Bağlı': 'Connected',
  'İçe Aktar': 'Import',
  'Yeni Trello Board Oluştur': 'Create New Trello Board',
  'Board adı…': 'Board name…',
  'Oluştur': 'Create',
});

const API = '/api/v1';

export type TrelloBoardOption = { id: string; name: string; url: string; linked: boolean; pano_id: number | null };

export function useTrelloStatus(clientId: number) {
  const [connected, setConnected] = useState<boolean | null>(null);
  const [username, setUsername] = useState('');
  const refresh = () => {
    axios.get(`${API}/trello/status`, { params: { client_id: clientId } })
      .then(r => { setConnected(r.data.connected); setUsername(r.data.member_username || ''); })
      .catch(() => setConnected(false));
  };
  useEffect(() => { refresh(); }, [clientId]);
  return { connected, username, refresh };
}

export function connectTrello(clientId: number, returnTo: 'gorevler' | 'panolar') {
  axios.get(`${API}/trello/connect`, { params: { client_id: clientId, return_to: returnTo } })
    .then(r => { window.location.href = r.data.auth_url; })
    .catch(() => {
      const lang = (localStorage.getItem('poyais_lang') as 'tr' | 'en') || 'tr';
      alert(lang === 'en' ? 'Could not start the Trello connection. Check that TRELLO_API_KEY/SECRET are configured.' : 'Trello bağlantısı başlatılamadı. TRELLO_API_KEY/SECRET tanımlı mı kontrol edin.');
    });
}

// URL'deki ?trello_oauth=success / ?trello_error=... parametresini bir kere işleyip temizler.
export function useTrelloOAuthResult(onSuccess: () => void) {
  const { t } = useLang();
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const ok = qs.get('trello_oauth');
    const err = qs.get('trello_error');
    if (ok || err) {
      if (ok === 'success') onSuccess();
      if (err) alert(t('Trello bağlantısı başarısız oldu: ') + err);
      qs.delete('trello_oauth'); qs.delete('trello_error');
      const rest = qs.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function ModalShell({ title, subtitle, onClose, children, width = 480 }: { title: string; subtitle?: string; onClose: () => void; children: React.ReactNode; width?: number }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,22,43,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: 18, padding: 26, width, maxWidth: '100%', maxHeight: '86vh', overflowY: 'auto' as const, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: T.text }}>
              <Icon.trello size={17} color="#0052CC" />{title}
            </div>
            {subtitle && <div style={{ fontSize: 12, color: T.textDim, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.textDim, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ConnectGate({ clientId, returnTo }: { clientId: number; returnTo: 'gorevler' | 'panolar' }) {
  const { t } = useLang();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '18px 4px', textAlign: 'center' as const }}>
      <span style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--py-blue-tint)', color: '#0052CC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon.trello size={22} color="#0052CC" />
      </span>
      <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.5 }}>
        {t("Trello hesabını bağla — board'larını Panolar'a aktar veya kartlarını Görevler ile eşleştir.")}
      </div>
      <Button variant="solid" onClick={() => connectTrello(clientId, returnTo)}>{t('Trello Hesabını Bağla')}</Button>
    </div>
  );
}

// ── Görevler: tekil kart çek / gönder (bridge) ───────────────────────────
export function TrelloBridgeModal({ clientId, onClose, onImported }: { clientId: number; onClose: () => void; onImported: () => void }) {
  const { t } = useLang();
  const { connected, username } = useTrelloStatus(clientId);
  const [boards, setBoards] = useState<TrelloBoardOption[]>([]);
  const [boardId, setBoardId] = useState('');
  const [tab, setTab] = useState<'pull' | 'push'>('pull');
  const [cards, setCards] = useState<any[]>([]);
  const [lists, setLists] = useState<any[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [localTasks, setLocalTasks] = useState<any[]>([]);
  const [pushTaskId, setPushTaskId] = useState('');
  const [pushListId, setPushListId] = useState('');

  useEffect(() => {
    if (connected) {
      axios.get(`${API}/trello/available-boards`, { params: { client_id: clientId } }).then(r => setBoards(r.data)).catch(() => setBoards([]));
      axios.get(`${API}/gorevler/`, { params: { client_id: clientId } }).then(r => setLocalTasks(r.data.filter((t: any) => !t.trello_card_id))).catch(() => {});
    }
  }, [connected, clientId]);

  useEffect(() => {
    if (!boardId) { setCards([]); setLists([]); return; }
    axios.get(`${API}/trello/bridge/cards`, { params: { client_id: clientId, trello_board_id: boardId } }).then(r => setCards(r.data)).catch(() => setCards([]));
    axios.get(`${API}/trello/bridge/lists`, { params: { client_id: clientId, trello_board_id: boardId } }).then(r => setLists(r.data)).catch(() => setLists([]));
    setSelected(new Set());
  }, [boardId, clientId]);

  const board = boards.find(b => b.id === boardId);
  const toggle = (id: string) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const pull = () => {
    if (!selected.size) return;
    setBusy(true);
    const chosen = cards.filter(c => selected.has(c.id));
    axios.post(`${API}/trello/bridge/pull`, {
      client_id: clientId, trello_board_id: boardId, trello_board_name: board?.name || '', cards: chosen,
    }).then(() => { onImported(); onClose(); }).catch(() => alert(t('Kartlar çekilemedi.'))).finally(() => setBusy(false));
  };

  const push = () => {
    if (!pushTaskId || !pushListId) return;
    setBusy(true);
    axios.post(`${API}/trello/bridge/push`, {
      client_id: clientId, task_id: Number(pushTaskId), trello_board_id: boardId, trello_board_name: board?.name || '', trello_list_id: pushListId,
    }).then(() => { onImported(); onClose(); }).catch(() => alert(t("Görev Trello'ya gönderilemedi."))).finally(() => setBusy(false));
  };

  return (
    <ModalShell title="Trello" subtitle={connected ? `${t('Bağlı hesap')}: @${username}` : undefined} onClose={onClose} width={520}>
      {connected === null ? (
        <div style={{ fontSize: 13, color: T.textDim }}>{t('Kontrol ediliyor…')}</div>
      ) : !connected ? (
        <ConnectGate clientId={clientId} returnTo="gorevler" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Trello Board')}</div>
            <select value={boardId} onChange={e => setBoardId(e.target.value)}
              style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: T.surface, boxSizing: 'border-box' as const }}>
              <option value="">{t('Board seç…')}</option>
              {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>

          {boardId && (
            <>
              <div style={{ display: 'flex', gap: 3, background: 'var(--py-track-bg)', borderRadius: 10, padding: 3 }}>
                {(['pull', 'push'] as const).map(v => (
                  <span key={v} onClick={() => setTab(v)} style={{ flex: 1, textAlign: 'center' as const, padding: '7px 14px', borderRadius: 8, fontSize: 12.5,
                    fontWeight: tab === v ? 700 : 600, color: tab === v ? T.text : 'var(--py-text-mid-alt)', cursor: 'pointer',
                    background: tab === v ? T.surface : 'transparent', boxShadow: tab === v ? '0 1px 2px rgba(20,22,43,.1)' : 'none' }}>
                    {v === 'pull' ? t("Trello'dan Çek") : t("Trello'ya Gönder")}
                  </span>
                ))}
              </div>

              {tab === 'pull' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ maxHeight: 260, overflowY: 'auto' as const, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cards.length === 0 && <div style={{ fontSize: 12, color: T.textDim }}>{t('Kart bulunamadı.')}</div>}
                    {cards.map(c => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 9,
                        opacity: c.already_imported ? 0.5 : 1, cursor: c.already_imported ? 'default' : 'pointer' }}>
                        <input type="checkbox" disabled={c.already_imported} checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                        <span style={{ fontSize: 12.5, color: T.text, flex: 1 }}>{c.name}</span>
                        {c.already_imported && <span style={{ fontSize: 10.5, color: T.textFaint }}>{t('zaten aktarıldı')}</span>}
                      </label>
                    ))}
                  </div>
                  <Button variant="solid" onClick={pull} disabled={!selected.size || busy}>
                    {busy ? t('Aktarılıyor…') : `${t('Seçilenleri Çek')} (${selected.size})`}
                  </Button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>{t('Görev')}</div>
                    <select value={pushTaskId} onChange={e => setPushTaskId(e.target.value)}
                      style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }}>
                      <option value="">{t('Görev seç…')}</option>
                      {localTasks.map((lt: any) => <option key={lt.id} value={lt.id}>{lt.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>{t('Trello Listesi')}</div>
                    <select value={pushListId} onChange={e => setPushListId(e.target.value)}
                      style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }}>
                      <option value="">{t('Liste seç…')}</option>
                      {lists.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                  <Button variant="solid" onClick={push} disabled={!pushTaskId || !pushListId || busy}>
                    {busy ? t('Gönderiliyor…') : t("Trello'ya Gönder")}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </ModalShell>
  );
}

// ── Panolar: tam board bağla / oluştur ───────────────────────────────────
export function TrelloBoardPickerModal({ clientId, onClose, onLinked }: { clientId: number; onClose: () => void; onLinked: () => void }) {
  const { t } = useLang();
  const { connected, username } = useTrelloStatus(clientId);
  const [boards, setBoards] = useState<TrelloBoardOption[]>([]);
  const [busyId, setBusyId] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = () => {
    axios.get(`${API}/trello/available-boards`, { params: { client_id: clientId } }).then(r => setBoards(r.data)).catch(() => setBoards([]));
  };
  useEffect(() => { if (connected) load(); }, [connected, clientId]);

  const link = (b: TrelloBoardOption) => {
    setBusyId(b.id);
    axios.post(`${API}/trello/link-board`, { client_id: clientId, trello_board_id: b.id, trello_board_name: b.name, create_pano: true })
      .then(() => { onLinked(); onClose(); }).catch(() => alert(t('Board içe aktarılamadı.'))).finally(() => setBusyId(''));
  };

  const createNew = () => {
    if (!newName.trim()) return;
    setCreating(true);
    axios.post(`${API}/trello/create-board`, { client_id: clientId, name: newName.trim() })
      .then(() => { onLinked(); onClose(); }).catch(() => alert(t("Trello'da board oluşturulamadı."))).finally(() => setCreating(false));
  };

  return (
    <ModalShell title={t('Trello Board Bağla')} subtitle={connected ? `${t('Bağlı hesap')}: @${username}` : undefined} onClose={onClose} width={480}>
      {connected === null ? (
        <div style={{ fontSize: 13, color: T.textDim }}>{t('Kontrol ediliyor…')}</div>
      ) : !connected ? (
        <ConnectGate clientId={clientId} returnTo="panolar" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' as const }}>
            {boards.length === 0 && <div style={{ fontSize: 12, color: T.textDim }}>{t('Trello hesabında board bulunamadı.')}</div>}
            {boards.map(b => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', border: `1px solid ${T.border}`, borderRadius: 10 }}>
                <Icon.trello size={15} color="#0052CC" />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: T.text, flex: 1 }}>{b.name}</span>
                {b.linked ? (
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: T.greenText, background: 'var(--py-green-tint)', padding: '3px 9px', borderRadius: 999 }}>{t('Bağlı')}</span>
                ) : (
                  <button onClick={() => link(b)} disabled={busyId === b.id}
                    style={{ fontSize: 11.5, fontWeight: 700, color: T.accentDeep, background: 'rgba(201,168,76,.1)', border: 'none', borderRadius: 8, padding: '6px 11px', cursor: 'pointer' }}>
                    {busyId === b.id ? t('Aktarılıyor…') : t('İçe Aktar')}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div style={{ paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{t('Yeni Trello Board Oluştur')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={t('Board adı…')}
                style={{ flex: 1, border: `1px solid ${T.borderMid}`, borderRadius: 9, padding: '8px 11px', fontSize: 12.5, fontFamily: 'inherit', outline: 'none' }} />
              <button onClick={createNew} disabled={!newName.trim() || creating}
                style={{ padding: '8px 14px', borderRadius: 9, border: 'none', background: 'var(--py-ink)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {creating ? '…' : t('Oluştur')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
